/**
 * feature/landing — spec §F4, the Overview splash.
 *
 * An **overlay**, not a route: §F4 requires the live app visible behind the backdrop, so
 * this mounts as a sibling of `<AppRoutes/>` in `App.tsx` and the URL never changes. The
 * left column is a scrolling summary of `/stats/overview`; the right is a second Leaflet
 * map of MELS activity locations (`MiniMap`).
 *
 * **What it counts.** §D1's payload keys still say "sites", and every number behind them is
 * now a MELS activity — the programme's real record of where it works, instead of the demo
 * scatter §C `sites` holds (see `apps/meta/views.py::stats_overview`). The four tiles are
 * located activities, distinct units reached, B.2.1 producers cumulative, and the
 * catalogue's own two counts.
 *
 * Three deviations from the mockup, each because the mockup is a demo and this is not:
 *
 * * **ESC closes it.** The reference has no key handler at all — a modal-shaped overlay
 *   with no keyboard exit is a trap for anyone not using a mouse.
 * * **A marker click sets the country.** The reference flies the Explorer to a bounding box
 *   and leaves the global country filter alone, so the reader lands on Mali with the
 *   filter still reading "All countries" and every panel showing programme totals.
 * * **The tour button closes first.** In the reference, `tourStart()`'s first two steps are
 *   `view:'home'`, so the splash stays up with the tour's callout drawn on top of it.
 *
 * Since the 2026-08-22 client review the splash is the session's *second* page, not its
 * first: `SignInGate` comes before it (R1), and the mini-map shades the eleven intervention
 * watersheds instead of scattering activity markers (R2, see `MiniMap`).
 */
import { useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { BarsV, Donut, Spark } from '../../components/charts';
import { fetchOverview, queryKeys, type OverviewKey, type StatsOverview } from '../../lib/api';
import { fmtCompact, fmtNum } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { COUNTRY_NAMES, useAppStore, type Country } from '../../lib/state';
import { activityTotal, countryBars, donutSlices } from './activityPoints';
import { MiniMap } from './MiniMap';

/** The consortium credit along the bottom of the backdrop — the mockup's, verbatim. */
const PARTNERS = 'IFDC · SNV · WUR · ICRAF · ISRIC · IITA';

export function SplashLanding() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const splashOpen = useAppStore((s) => s.splashOpen);
  /**
   * R1: the sign-in gate is the session's *first* page, and the splash waits behind it.
   *
   * Not merely hidden — `open` gates the `/stats/overview` query, the ESC handler and the
   * render alike, so while the gate is up the splash costs nothing and, more to the point,
   * cannot consume the Escape key. Without that last part ESC "does not skip the gate" but
   * silently dismissed the splash *behind* it, and a reader who pressed it landed straight
   * in the app having never seen the landing page.
   */
  const gateOpen = useAppStore((s) => s.gateOpen);
  const open = splashOpen && !gateOpen;
  const setSplashOpen = useAppStore((s) => s.setSplashOpen);
  const country = useAppStore((s) => s.country);
  const setCountry = useAppStore((s) => s.setCountry);
  const years = useAppStore((s) => s.years);

  const { data } = useQuery<StatsOverview, Error, StatsOverview, OverviewKey>({
    queryKey: queryKeys.overview(country),
    queryFn: fetchOverview,
    // The splash is a first paint; nothing else in the app is waiting on this number, and
    // re-fetching it every time the overlay is reopened would put a request on the critical
    // path of a button click. One minute matches the server's own memo.
    staleTime: 60_000,
    enabled: open,
  });

  const close = useCallback(() => setSplashOpen(false), [setSplashOpen]);

  /**
   * ESC closes — the pattern `DrawAoi`/`Measure`/`SearchBox` already use.
   *
   * Bound only while the overlay is open, so it cannot swallow an Escape meant for the
   * search box or a draw tool underneath it.
   */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  const goto = useCallback(
    (path: string) => {
      close();
      navigate(path);
    },
    [close, navigate],
  );

  const pickCountry = useCallback(
    (iso3: Country) => {
      // §F4's contract is "click → close splash, set country, fly Map Explorer", and the
      // fly is `CountryFocus`'s (`features/map/HierarchyLayers`), which is mounted **only
      // under `/`**. So the navigation is not decoration: the splash is reachable from the
      // Overview nav on any route, and without this a marker click from `/dashboard` closed
      // the overlay, changed the global country filter under the reader, and flew nothing —
      // while the hero CTA eight lines up already went to `/`. Setting the country first
      // means `CountryFocus` has it on mount when the route does change.
      setCountry(iso3);
      goto('/');
    },
    [goto, setCountry],
  );

  if (!open) return null;

  const slices = donutSlices(data);
  const bars = countryBars(data);
  const total = activityTotal(data);
  const scoped = country !== 'ALL';
  const countryName = scoped
    ? t(`country.${country}`, COUNTRY_NAMES[country] ?? country)
    : '';

  /**
   * The four tiles. `fmtCompact` on the producer count only: 97,967 becomes "98k", which is
   * the mockup's own register for that tile ("46.8k") and keeps a 23px mono number on one
   * line. The other three are small enough to print in full, and a rounded count of
   * datasets would be a strange thing to round.
   */
  const tiles: { key: string; value: string }[] = [
    { key: 'located', value: fmtNum(data?.operation_points ?? 0, 0) },
    { key: 'communes', value: fmtNum(data?.communes_reached ?? 0, 0) },
    { key: 'farmers', value: fmtCompact(data?.farmers_engaged ?? 0) },
    {
      key: 'catalogue',
      value: `${fmtNum(data?.datasets ?? 0, 0)} / ${fmtNum(data?.indicators ?? 0, 0)}`,
    },
  ];

  return (
    <div
      id="landOv"
      className="land-splash"
      role="dialog"
      aria-modal="true"
      aria-labelledby="landTitle"
      data-testid="splash"
    >
      <div id="landCard">
        <div id="homeContent">
          <div id="hpLeft">
            <div className="hp-head">
              <span className="hp-logo">
                <img src="/logo.png" alt="" width={92} height={92} />
              </span>
              <div>
                <h2 id="landTitle">{t('splash.title')}</h2>
                <p className="hp-sub">{t('splash.lede')}</p>
              </div>
            </div>

            <div className="hp-stats" data-testid="splash-stats">
              {tiles.map((tile) => (
                <div className="hp-stat" key={tile.key} data-stat={tile.key}>
                  <b>{tile.value}</b>
                  <span>{t(`splash.stat.${tile.key}`)}</span>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="btn primary hp-hero-btn"
              id="hpExplore"
              onClick={() => goto('/')}
            >
              <Icon name="map" size={19} />
              {t('splash.cta.explore')}
            </button>
            <div className="hp-ctas">
              <button
                type="button"
                className="btn ghost"
                id="hpDash"
                onClick={() => goto('/dashboard')}
              >
                <Icon name="chart" size={14} />
                {t('splash.cta.dashboard')}
              </button>
              {/* §F11's guided tour is not built yet. The button exists because §F4 puts it
                  here, and it closes the splash — which is the half of "take the tour" that
                  is already true — rather than pretending to start something. */}
              <button type="button" className="btn ghost" id="hpTour" onClick={close}>
                <Icon name="info" size={14} />
                {t('splash.cta.tour')}
              </button>
            </div>
          </div>

          <div id="hpRight">
            <div className="hp-charts2">
              <div>
                <h4>{t('splash.chart.byStatus')}</h4>
                <div className="hp-chart hp-donut-row">
                  <Donut
                    items={slices}
                    size={122}
                    centerLabel={fmtNum(total, 0)}
                    centerSub={t('splash.chart.donutSub')}
                    fmt={0}
                    ariaLabel={t('splash.chart.byStatus')}
                  />
                  <div className="hp-mini-legend">
                    {slices.map((slice) => (
                      <span key={slice.status} data-legend={slice.status}>
                        <i style={{ background: slice.color }} />
                        {slice.label} · <b>{fmtNum(slice.value, 0)}</b>
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                {/* The mockup's country/commune swap. With one country selected a bar chart
                    of one bar says nothing the tile above it has not already said, so the
                    space goes to the number that only makes sense in a single scope. */}
                <h4>
                  {scoped
                    ? t('splash.chart.communesIn', { country: countryName })
                    : t('splash.chart.byCountry')}
                </h4>
                {scoped ? (
                  <div className="hp-chart" style={{ paddingTop: 18 }}>
                    <span className="ipo-val">{fmtNum(data?.communes_reached ?? 0, 0)}</span>
                    <div className="hp-note">
                      {t('splash.chart.communesNote', { country: countryName })}
                    </div>
                  </div>
                ) : (
                  <div className="hp-chart">
                    <BarsV
                      labels={bars.labels}
                      values={bars.values}
                      colors={bars.colors}
                      h={130}
                      fmt={0}
                      ariaLabel={t('splash.chart.byCountry')}
                    />
                  </div>
                )}
              </div>
            </div>

            <h4>{t('splash.chart.headline')}</h4>
            <div className="hp-chart">
              {/* `Spark` is fluid (`width:100%` + viewBox) unlike the mockup's literal
                  560px, so "full width" survives the 96vw floor. */}
              <Spark
                series={data?.headline_trend.values ?? []}
                w={560}
                h={76}
                color="#2E7D32"
                unit={t('splash.chart.producers')}
                labels={years}
                fmt={0}
                ariaLabel={t('splash.chart.headline')}
              />
            </div>
            <div className="hp-note">{t('splash.liveness')}</div>
          </div>
        </div>

        <MiniMap onPick={pickCountry} />

        <button
          type="button"
          id="landClose"
          title={t('splash.close')}
          aria-label={t('splash.close')}
          onClick={close}
        >
          <Icon name="x" size={16} />
        </button>
      </div>
      {/* Backdrop clicks deliberately do **not** close: the mockup wires that for its modal
          mode only, and an accidental click outside a full-screen landing card should not
          dismiss the thing the reader has not read yet. ESC and ✕ are the exits. */}
      <div id="landFoot">{`${PARTNERS} — ${t('splash.programme')}`}</div>
    </div>
  );
}

export default SplashLanding;
